/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import React from 'react'
import classNames from 'classnames/bind'
import { Text } from 'hocs/localization'
import { ROUTES } from 'constants/routes'
import ProfileContent from 'apps/ProfileContent'
import BusinessDetailsForm from 'apps/Profile/BusinessDetailsForm'
import PaymentDetailsForm from 'apps/Profile/PaymentDetailsForm'
import ShippingInfoForm from 'apps/Profile/ShippingInfoForm'
import ManageAccount from 'apps/ManageAccount'
import CurrentSubscription from 'apps/CurrentSubscription'
import CollapsibleBlock from 'elements/CollapsibleBlock'
import Notifications from './apps/Notifications'
import ShippingHistory from './apps/ShippingHistory'
import PaymentHistory from './apps/PaymentHistory'
import { onVerifyCurrentCard } from 'redux-app/actionCreators/users'
import { onShowError } from 'redux-app/modules/alerts'
import { SUBSCRIPTION_PLAN_TYPE } from 'constants/subscription'
import { history } from 'utils/location'
import * as styles from './ViewProfile.scss'

const cx = classNames.bind(styles)

interface IProps {
  t: Text,
  isBusiness: boolean,
  isRenewed: boolean,
  hasPreviousContract: boolean,
  contractType: string,
  hasExtensionDocument: boolean,
  futureContractType: string,
  downloadTerms: () => void,
  enrollPlanProcess: () => void,
  renewPlanProcess: () => void,
  downloadRenewedTerms: () => void
}

interface IState {
  isAddCardFormSubmitted: boolean,
  isAddCardFormOpen: boolean,
  isShippingHistoryOpen: boolean,
  isPaymentHistoryOpen: boolean,
}

class ViewProfile extends React.Component<IProps, IState> {
  private paymentHistoryRef: React.RefObject<any>

  public readonly state = {
    isAddCardFormSubmitted: false,
    isAddCardFormOpen: false,
    isShippingHistoryOpen: false,
    isPaymentHistoryOpen: false,
  }

  private afterCardSubmit = () => {
    this.setState({
      isAddCardFormOpen: false,
    })
    window.scrollTo(0, 0)
  }

  private onOpenCardForm = () => {
    this.setState({
      isAddCardFormOpen: true,
    })
  }

  private onCloseCardForm = () => {
    this.setState({
      isAddCardFormOpen: false,
    })
  }

  private onOpenShippingBlock = () => {
    this.setState({
      isShippingHistoryOpen: true,
    })
  }

  private onCloseShippingBlock = () => {
    this.setState({
      isShippingHistoryOpen: false,
    })
  }

  private onOpenPaymentBlock = () => {
    this.setState({
      isPaymentHistoryOpen: true,
    })
  }

  private onClosePaymentBlock = () => {
    this.setState({
      isPaymentHistoryOpen: false,
    })
  }

  private onGoToPaymentHistory = event => {
    event.preventDefault()
    const { top } = this.paymentHistoryRef.getBoundingClientRect()

    this.setState({
      isPaymentHistoryOpen: true,
    }, () => {
      window.scrollTo({
        top: window.pageYOffset + top,
        behavior: 'smooth',
      })
    })
  }

  private onGoToChangePlanProcess = event => {
    event.preventDefault()
    return history.push(ROUTES.profile.changePlan.root)
  }

  public render () {
    const {
      t,
      currentPlan,
      isLoading,
      isBusiness,
      isRenewed,
      hasPreviousContract,
      contractType,
      futureContractType,
      hasExtensionDocument,
      downloadTerms,
      enrollPlanProcess,
      renewPlanProcess,
      downloadRenewedTerms
    } = this.props

    const isCardBlockOpen = this.state.isAddCardFormOpen
    const { isShippingHistoryOpen, isPaymentHistoryOpen } = this.state

    return (
      <ProfileContent isLoading={isLoading}>
        <div className={cx('container', styles.container768)}>
          <div className='row'>
            <div className='col-md-12'>
              <div className={styles.title}>
                <h2>{t('heading')}</h2>
              </div>
            </div>
          </div>
          <div className={cx('row', styles.topSpace40)}>
            <div className='col-md-12'>
              <Notifications onGoToPaymentHistory={this.onGoToPaymentHistory}
              onGoToRenewalProcess={renewPlanProcess} onGoToEnrollPlanProcess={enrollPlanProcess} />
              <CurrentSubscription plan={currentPlan} onGoToEnrollPlanProcess={enrollPlanProcess}
              onGoToChangePlanProcess={this.onGoToChangePlanProcess} onGoToRenewalProcess={renewPlanProcess} />

              <CollapsibleBlock
                styleType='topSpace40'
                title={t('shippingHistory')}
                onOpen={this.onOpenShippingBlock}
                onClose={this.onCloseShippingBlock}
              >
                {isShippingHistoryOpen ? (<ShippingHistory/>) : null}
              </CollapsibleBlock>

              <CollapsibleBlock
                isOpen={isPaymentHistoryOpen}
                setRef={el => this.paymentHistoryRef = el}
                styleType='topSpace40'
                title={t('paymentHistory')}
                onOpen={this.onOpenPaymentBlock}
                onClose={this.onClosePaymentBlock}
              >
                {isPaymentHistoryOpen ? (<PaymentHistory/>) : null}
              </CollapsibleBlock>

              <CollapsibleBlock
                styleType='topSpace40'
                title={t('editShippingTitle')}
              >
                <ShippingInfoForm isEdit/>
              </CollapsibleBlock>

              {isBusiness && (
                <CollapsibleBlock
                  styleType='topSpace40'
                  title={t('editBusinessDetails')}
                >
                  <BusinessDetailsForm isEdit/>
                </CollapsibleBlock>
              )}

              <CollapsibleBlock
                isOpen={isCardBlockOpen}
                styleType='topSpace40'
                title={t('editPaymentTitle')}
                onOpen={this.onOpenCardForm}
                onClose={this.onCloseCardForm}
              >
                <PaymentDetailsForm isOpen={isCardBlockOpen} afterSubmit={this.afterCardSubmit} isEdit/>
              </CollapsibleBlock>

              <CollapsibleBlock
                styleType='topSpace40'
                title={t('manageAccountTitle')}
              >
                <ManageAccount />
              </CollapsibleBlock>

              <div className={styles.link} onClick={downloadTerms}>
                {(isRenewed && contractType == SUBSCRIPTION_PLAN_TYPE.contract) ? t('downloadOriginalLink') : t('downloadLink')}
              </div>

              {(hasExtensionDocument ||
                futureContractType === SUBSCRIPTION_PLAN_TYPE.contract || 
               (futureContractType === '' && ((isRenewed && contractType == SUBSCRIPTION_PLAN_TYPE.contract) || (hasPreviousContract && contractType == SUBSCRIPTION_PLAN_TYPE.contract))) 
               ) && (
              <div className={styles.link} onClick={downloadRenewedTerms}>
                  {t('downloadExtensionLink')}
              </div>)}

            </div>
          </div>
        </div>
      </ProfileContent>
    )
  }
}

export default ViewProfile
